import {parseNumber} from '@benchling/js-utils';

import {DataTableConfig} from 'notebook/data-tables/DataTableConstants';
import {
  ColumnDefinition,
  getColumnDefinitionIndex,
} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import ArgType from 'notebook/tables/excess/ArgType';
import * as ast from 'notebook/tables/excess/AST';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import {Environment} from 'notebook/tables/excess/Evaluator';
import * as ExcessUtil from 'notebook/tables/excess/Util';
import {ExternalCache} from 'notebook/tables/lookup-field/LookupFieldCacheConstants';
import {
  BaseLookupDefinition,
  LookupFieldResult,
  LookupSpecifier,
  LookupType,
  SampleSpecifier,
} from 'notebook/tables/lookup-field/LookupFieldDefinitionConstants';
import {
  LOOKUP_FIELD_SOURCE,
  parseLookupFieldArgValues,
  parseLookupSpecifierFromLookupFieldNode,
} from 'notebook/tables/lookup-field/LookupFieldDefinitionParser';
import {
  getLookupFieldResultsFromCache,
  pickSampleSpecifier,
} from 'notebook/tables/lookup-field/LookupFieldUtil';
import {convertTagToTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, RawValue, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import legacyRunModal from 'ui-library/legacyRunModal';

function guessValueTypeBasedOnValue(value: RawValue): string {
  // For backwards compatibility handle both, numeric and string values
  // @ts-expect-error
  const isNumber = typeof value === 'string' ? parseNumber(value) != null : !Number.isNaN(parseFloat(value));
  return isNumber ? ExcessUtil.VALUE_TYPES.NUMBER : ExcessUtil.VALUE_TYPES.STRING;
}

function parseNumericValue(value: RawValue): RawValue | null {
  return typeof value === 'string' ? parseNumber(value) : value;
}

function convertCachedResultToExcessValue(lookupFieldResult: LookupFieldResult): TypedValue {
  const {type, value, tagValue} = lookupFieldResult;
  if (tagValue) {
    return convertTagToTypedValue(tagValue);
  }
  // For backwards compatibility guess the type based on the value if missing
  const resolvedType = type || guessValueTypeBasedOnValue(value);
  const rawValue = (ExcessUtil.NUMERIC_TYPES as Array<string>).includes(resolvedType)
    ? // For backwards compatibility support numeric value as well. Normally the value should be string
      parseNumericValue(value)
    : value;
  return {rawValue, type: resolvedType} as TypedValue;
}

function asArray(values: Array<TypedValue>): TypedValue {
  return {
    type: BaseTypedValueType.ARRAY,
    rawValue: values,
  };
}

export default {
  name: 'LOOKUPFIELD',
  category: null,
  shortDescription: "Look up a value of a row's Benchling entity.",
  params: [],
  description: "Look up a value of a row's Benchling entity.",
  repeatableLastParamType: ArgType.any,
  repeatableLastParamDescription: 'Arguments.', // TODO
  returnType: ExcessUtil.VALUE_TYPES.ARRAY,

  convertCachedResultToExcessValue,

  /**
   * Evaluate the function using the values stored in the externalCache for lookupDefinition.
   *
   * If no matching results are found in the cache, `NotApplicableError` is raised.
   *
   * @param  lookupSpecifierWithResolvedSamples - lookup specifier with resolved source.
   *                                              See: LookupFieldDefinitionParser.parseLookupFieldArgValues
   *                                              for the exact format. If sampleSpecifier is missing it means
   *                                              that this is the top level lookup field and the sample is
   *                                              taken from rowData.
   */
  evalWithEnv(
    env: Environment,
    lookupSpecifierWithResolvedSamples: LookupSpecifier & {
      sampleSpecifiers: Array<SampleSpecifier> | null;
    }
  ): TypedValue {
    const {externalCache, rowData} = env;
    let {sampleSpecifiers} = lookupSpecifierWithResolvedSamples;
    if (
      sampleSpecifiers == null &&
      // Templates have no rowData or sampleSpecifier
      rowData != null
    ) {
      sampleSpecifiers = [pickSampleSpecifier(rowData)];
    } else if (sampleSpecifiers == null) {
      throw new ExcessUtil.NotApplicableError('No values are found.');
    } else if (sampleSpecifiers.length === 0) {
      // Source LOOKUPFIELD didn't return any samples
      return asArray([]);
    }

    let lookupFieldResults: Array<LookupFieldResult> = [];
    for (const sampleSpecifier of sampleSpecifiers) {
      let results = getLookupFieldResultsFromCache({
        externalCache,
        lookupSpecifier: lookupSpecifierWithResolvedSamples,
        sampleSpecifier,
      });
      if (results == null) {
        if (sampleSpecifiers.length > 1) {
          // This is for backwards compatibility to make sure we are rendering lookupfields that
          // have multiple final values. Previously we were storing these values in cache with a single
          // empty sample specifier instead of storing each result with the sample specifier it belongs to
          results = getLookupFieldResultsFromCache({
            externalCache,
            lookupSpecifier: lookupSpecifierWithResolvedSamples,
            sampleSpecifier: {},
          });
          if (results != null) {
            return asArray(
              results.map((lookupFieldResult) => convertCachedResultToExcessValue(lookupFieldResult))
            );
          }
        }
        throw new ExcessUtil.NotApplicableError('No values are found.');
      }
      lookupFieldResults = [...lookupFieldResults, ...results];
    }

    return asArray(
      lookupFieldResults.map((lookupFieldResult) => convertCachedResultToExcessValue(lookupFieldResult))
    );
  },

  /**
   * Parse the arguments and return a lookupSpecifierWithResolvedSamples.
   *
   * See: LookupFieldUtil.getLookupFieldResultsFromCache for the exact format. The output of this method
   * is later used as the input of evalWithEnv.
   */
  processArgValues(
    ...args: Array<TypedValue>
  ): [BaseLookupDefinition & {sampleSpecifiers: Array<SampleSpecifier> | null}] {
    return parseLookupFieldArgValues(args);
  },

  getExtraChildren({
    rowIndex,
    columnDefinitions,
    node,
  }: {
    rowIndex: number;
    columnDefinitions: Array<ColumnDefinition | null> | null;
    node: ast.FunctionOperator;
  }): Array<ast.ASTNode> {
    let lookupSpecifier;
    try {
      lookupSpecifier = parseLookupSpecifierFromLookupFieldNode(node);
    } catch (e) {
      if (e instanceof ExcessUtil.FormulaParseError) {
        return [];
      }
      throw e;
    }
    if (lookupSpecifier == null || lookupSpecifier.sourceSpecifier != null) {
      // If the function already contains SOURCE argument, no extra children are needed
      return [];
    }
    if (columnDefinitions == null) {
      return [];
    }
    const stageRunColumnIndex = getColumnDefinitionIndex({
      columnDefinitionType: TableColumnType.STAGE_RUN,
      tableColumnDefinitions: columnDefinitions,
      isOptional: true,
    });
    if (stageRunColumnIndex < 0) {
      return [];
    }
    const stageRunCellId = {
      row: rowIndex,
      column: stageRunColumnIndex,
    };
    return [
      new ast.Value({
        type: BaseTypedValueType.STRING,
        rawValue: LOOKUP_FIELD_SOURCE,
      }),
      new ast.Cell({
        id: stageRunCellId,
        text: 'Stage Run',
        locks: {
          row: false,
          col: false,
        },
      }),
    ];
  },

  async onCreateFormula({
    entryType,
    stageConfigurationId,
    supportedLookupTypes,
    rowData,
    onUpdateSelectedCell,
    dataTableConfig,
    evaluatedRow,
    columnDefinitions,
  }: {
    entryType: string;
    stageConfigurationId: string | null;
    supportedLookupTypes: Array<LookupType>;
    rowData: RowData | null;
    onUpdateSelectedCell: (cellData: Partial<Cell>) => void;
    dataTableConfig?: DataTableConfig;
    evaluatedRow?: Array<EvaluatedCell> | null;
    columnDefinitions?: Array<ColumnDefinition | null> | null;
  }): Promise<{formula: string; cursorPosition: number} | null> {
    const ConfigureLookupFieldModal =
      require('notebook/tables/lookup-field/ConfigureLookupFieldModal').default;
    const result = (await legacyRunModal<typeof ConfigureLookupFieldModal>(ConfigureLookupFieldModal, {
      entryType,
      stageConfigurationId,
      supportedLookupTypes,
      rowData,
      dataTableConfig,
      evaluatedRow,
      columnDefinitions,
    })) as {formula: string; newExternalCache: ExternalCache} | null;
    if (result) {
      onUpdateSelectedCell({
        externalCache: result.newExternalCache,
      });
      return {formula: result.formula, cursorPosition: result.formula.length};
    }
    return null;
  },
};
